/* Cours privés : les règles réglées par l'école (Réglages › Cours privés, Jen, 02.11.2026 :
   « tout ça dans les réglages, pour que je puisse repasser au travers avec l'école ») et les
   phrases qui en dépendent — les mêmes mots que les e-mails et les factures
   (backend/console/regles_prives.py). Avec les valeurs de départ, mot pour mot les phrases
   d'avant : « Annulation gratuite jusqu'à 24 h avant ; ensuite, la moitié du prix reste due ».

   Les écrans reçoivent les règles du serveur (`regles` dans les réponses des cours privés) ; le
   site les reçoit aussi par `GET /api/reglages` (→ cours_prives), appliquées sur place ici
   (page Tarifs, FAQ, page Calendly de la production). Sans réponse : les valeurs de départ. */

export const REGLES_DEPART = {
  delai_heures: 24, horizon_jours: 61, horizon_texte: "2 mois", pause: 15, pas_debut: 30,
  ouverture: "07:00", fermeture: "22:00",
  annulation_heures: 24, annulation_pourcentage: 50,
  absence_pourcentage: 50, absence_pourcentage_max: 100,
  ecole_facturer_heures: 24, ecole_facturer_pourcentage: 50,
  deplacement_minutes: 60, deplacement_texte: "1 h",
  rappel_premier_jours: 30, rappel_ensuite_jours: 30,
};

/* Les règles d'une réponse, complétées par celles de départ (un vieux serveur n'en envoie pas). */
export const regles = (r) => ({ ...REGLES_DEPART, ...(r || {}) });

/* Les durées et prix de chaque discipline (repli : ceux de départ ; remplacés sur place par
   `appliquerCoursPrivesConsole`). */
export const DISCIPLINES_PRIVES = [
  { id: "chant", label: "Chant", durees: [{ minutes: 30, prix: 60 }, { minutes: 45, prix: 75 }, { minutes: 60, prix: 90 }] },
  { id: "theatre", label: "Théâtre", durees: [{ minutes: 30, prix: 60 }, { minutes: 45, prix: 75 }, { minutes: 60, prix: 90 }] },
  { id: "danse", label: "Danse", durees: [{ minutes: 60, prix: 120 }, { minutes: 90, prix: 150 }] },
];
export const REGLES_SITE = { ...REGLES_DEPART };

export function appliquerCoursPrivesConsole(d) {
  if (!d || !Array.isArray(d.disciplines)) return;
  d.disciplines.forEach((x) => {
    const cible = DISCIPLINES_PRIVES.find((y) => y.id === x?.id);
    const durees = (x?.durees || []).filter((y) => Number(y?.minutes) > 0 && Number(y?.prix) > 0)
      .map((y) => ({ minutes: Number(y.minutes), prix: Number(y.prix) }));
    if (cible && durees.length) cible.durees = durees;
  });
  Object.assign(REGLES_SITE, d.regles || {});
}

/* « 60.– », « 62.50 » : un prix réglé au centime reste lisible. */
export const franc = (p) => (Number.isInteger(Number(p)) ? `${Number(p)}.–` : Number(p).toFixed(2));

/* Le prix le plus bas (« Cours privés dès CHF 60 »). */
export const prixMin = () => Math.min(...DISCIPLINES_PRIVES.flatMap((d) => d.durees.map((x) => x.prix)));

/* La part du prix : 50 → « la moitié du prix » (féminin), 100 → « le prix du cours », sinon
   « 70 % du prix ». */
export const partDuPrix = (pct) => {
  const p = Number(pct);
  if (p === 50) return { texte: "la moitié du prix", f: true };
  if (p >= 100) return { texte: "le prix du cours", f: false };
  return { texte: `${p} % du prix`, f: false };
};
export const due = (f) => (f ? "due" : "dû");
export const facturee = (f) => (f ? "facturée" : "facturé");
export const maj = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
export const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

/* 60 → « 1 h », 90 → « 1 h 30 », 30 → « 30 min ». */
export const duree = (m) => {
  const n = Number(m);
  return n >= 60 ? `${Math.floor(n / 60)} h${n % 60 ? ` ${String(n % 60).padStart(2, "0")}` : ""}` : `${n} min`;
};
/* « moins d'1 h », « moins de 30 min ». */
export const moinsDe = (t) => (/^1 h\b/.test(t) ? `moins d'${t}` : `moins de ${t}`);
export const heures = (h) => `${Number(h)} h`;
/* 61 → « 2 mois », 28 → « 4 semaines », 45 → « 45 jours » (comme regles_prives.horizon). */
export const horizonTexte = (jours) => {
  const j = Number(jours);
  const mois = Math.round(j / 30.5);
  if (mois >= 1 && Math.abs(j - mois * 30.5) <= 1.5) return `${mois} mois`;
  if (j % 7 === 0) return `${j / 7} semaine${j > 7 ? "s" : ""}`;
  return `${j} jours`;
};
/* « 7h », « 16h30 ». */
export const heureFr = (h) => { const [a, b] = String(h || "").split(":"); return `${Number(a)}h${b && b !== "00" ? b : ""}`; };

/* « ensuite, la moitié du prix reste due » ; avec un montant : « … (CHF 30.00) reste due ». */
export const ensuiteDu = (R, montant) => {
  const { texte, f } = partDuPrix(R.annulation_pourcentage);
  return `${texte}${montant === undefined ? "" : ` (${chf((Number(montant) * R.annulation_pourcentage) / 100)})`} reste ${due(f)}`;
};
/* « Absent·e sans avoir annulé : la moitié du prix est facturée. » */
export const phraseAbsence = (R) => {
  const { texte, f } = partDuPrix(R.absence_pourcentage);
  return `Absent·e sans avoir annulé : ${texte} est ${facturee(f)}.`;
};
/* « Annulation gratuite jusqu'à 24 h avant ; ensuite, la moitié du prix reste due. Absent·e sans
   avoir annulé : la moitié du prix est facturée. » */
export const conditionsAnnulation = (R) => `Annulation gratuite jusqu'à ${heures(R.annulation_heures)} avant ; ensuite, ${ensuiteDu(R)}. ${phraseAbsence(R)}`;

/* La forme courte des écrans de l'école : « la moitié », « le prix entier », « 70 % du prix ». */
export const partCourte = (pct) => {
  const p = Number(pct);
  if (p === 50) return { texte: "la moitié", f: true };
  if (p >= 100) return { texte: "le prix entier", f: false };
  return { texte: `${p} % du prix`, f: false };
};

/* Les deux choix de « Absent·e — facturer » : celui proposé d'office (la règle publiée), puis le
   maximal (le prix entier au départ). Un seul si l'école les a égalés. `suite` : ce que la
   console ajoute à l'aide du premier (« ; une absence est une annulation au dernier moment »). */
export const choixAbsence = (R, suite = "") => {
  const a = Number(R.absence_pourcentage);
  const b = Number(R.absence_pourcentage_max);
  const titre = (p) => `${p} % — ${p === 50 ? "la moitié du prix" : p >= 100 ? "le prix entier" : `${p} % du prix`}`;
  // La règle publiée : celle de l'annulation tardive quand l'absence suit le même pourcentage
  // (une absence est une annulation au dernier moment), sinon celle de l'absence.
  const tard = partCourte(R.annulation_pourcentage);
  const absent = partDuPrix(a);
  const aide = Number(R.annulation_pourcentage) === a
    ? `La règle publiée : moins de ${heures(R.annulation_heures)} avant, ${tard.texte} est ${due(tard.f)}${suite}.`
    : `La règle publiée : absent·e sans avoir annulé, ${absent.texte} est ${facturee(absent.f)}.`;
  const choix = [[a, titre(a), aide]];
  if (b !== a) choix.push([b, titre(b), ""]);
  return choix;
};

/* Le salaire du prof d'un cours privé (Jen, 02.11.2026 au soir : « 30.– pour 30 minutes, 40.– pour
   45 minutes, 50.– pour 60 minutes, +5.– pour le déplacement … Le 50 %, c'est s'ils se sont
   déplacés ») — les écrans de l'école et l'app du prof seulement, JAMAIS les familles. La règle
   (`regle`) vient du serveur (`salaires.regle` : grille, deplacement, pour_rien_pourcentage,
   pour_rien_deplacement) ; sans réponse, celle de départ. */
export const SALAIRE_DEPART = {
  grille: [{ minutes: 30, salaire: 30 }, { minutes: 45, salaire: 40 }, { minutes: 60, salaire: 50 }],
  deplacement: 5, pour_rien_pourcentage: 50, pour_rien_deplacement: true,
};
export const regleSalaire = (x) => ({ ...SALAIRE_DEPART, ...(x || {}) });
/* « 30.– pour 30 min, 40.– pour 45 min, 50.– pour 60 min » (les mots de Jen). */
export const grilleTexte = (regle) => regleSalaire(regle).grille.map((x) => `${franc(x.salaire)} pour ${x.minutes} min`).join(", ");
/* « 50 % du salaire + le déplacement » (déplacé pour rien). */
export const pourRienTexte = (regle) => {
  const g = regleSalaire(regle);
  return `${g.pour_rien_pourcentage} % du salaire${g.pour_rien_deplacement && Number(g.deplacement) > 0 ? " + le déplacement" : ""}`;
};
/* Pourquoi le prof est payé pour un cours qu'il n'a pas donné (`motif` d'une ligne). */
export const motifSalaire = (motif, seuil = "moins d'1 h") => ({
  absent: "Famille absente",
  annule_tard: `Annulé ${seuil} avant, crédité`,
  annule_ecole: "Annulé par l'école, prof déplacé",
}[motif] || "Prof déplacé pour rien");
/* Le calcul d'une ligne : « 30 min · salaire 30.– + déplacement 5.– », ou « Famille absente ·
   50 % du salaire de 30 min + déplacement 5.– ». */
export const detailSalaire = (l, seuil) => {
  const depl = Number(l.deplacement) > 0 ? ` + déplacement ${franc(l.deplacement)}` : "";
  return l.pour_rien
    ? `${motifSalaire(l.motif, seuil)} · ${l.pourcentage} % du salaire de ${l.minutes} min${depl}`
    : `${l.minutes} min · salaire ${franc(l.salaire_duree)}${depl}`;
};

/* Les tarifs, une ligne par durée ; les disciplines aux mêmes prix partagent leurs lignes
   (« Chant / Théâtre · 1h »), comme la page Tarifs l'a toujours écrit. */
const LIBELLE_DUREE = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, "0") : ""}` : `${m} min`);
export function tarifsGroupes(disciplines = DISCIPLINES_PRIVES) {
  const groupes = [];
  disciplines.forEach((d) => {
    const cle = JSON.stringify(d.durees);
    const g = groupes.find((x) => x.cle === cle);
    if (g) g.labels.push(d.label); else groupes.push({ cle, labels: [d.label], durees: d.durees });
  });
  return groupes;
}
/* La page Tarifs : « Chant / Théâtre · 1h — 90 », du plus long au plus court, danse ensuite. */
export const tarifsPage = () => tarifsGroupes().flatMap((g) => [...g.durees].sort((a, b) => b.minutes - a.minutes)
  .map((x) => ({ type: `${g.labels.join(" / ")} · ${LIBELLE_DUREE(x.minutes)}`, price: x.prix })));
export const prixDe = (discipline, minutes, repli) => DISCIPLINES_PRIVES.find((d) => d.id === discipline)?.durees
  .find((x) => x.minutes === minutes)?.prix ?? repli;
